/**
 * Organisation Theme Hook
 * Fetches and manages organisation-specific theme configuration
 */

import { useState, useEffect, useCallback } from 'react';
import { BackendAuth } from '@/lib/auth-backend';

/**
 * Hook to fetch and manage organisation theme
 * @param {number} organisationId - Organisation ID
 * @returns {Object} Theme data and utility functions
 */
export function useOrganisationTheme(organisationId) {
  const [theme, setTheme] = useState(null);
  const [assets, setAssets] = useState({});
  const [cssVariables, setCssVariables] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  /**
   * Fetch theme configuration from backend
   */
  const fetchTheme = useCallback(async () => {
    if (!organisationId) {
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      const response = await BackendAuth.apiCallJson(`/api/theme/${organisationId}`);

      if (response.success) {
        setTheme(response.theme);
        setAssets(response.assets || {});
        setCssVariables(response.css_variables || {});

        // Apply CSS variables to DOM
        applyThemeToDOM(response.css_variables || {});

        // Apply theme class (e.g. `theme-technic`) so theme-scoped stylesheets activate
        applyThemeClass(response.theme?.base_theme);
      } else {
        throw new Error(response.message || 'Failed to fetch theme');
      }
    } catch (err) {
      console.error('Error fetching organisation theme:', err);
      setError(err.message);

      // Load default theme on error
      loadDefaultTheme();
    } finally {
      setIsLoading(false);
    }
  }, [organisationId]);

  /**
   * Update theme configuration
   * @param {Object} themeData - Theme configuration to update
   */
  const updateTheme = useCallback(async (themeData) => {
    try {
      const response = await BackendAuth.apiCallJson(`/api/theme/${organisationId}`, {
        method: 'PUT',
        body: JSON.stringify(themeData)
      });

      if (response.success) {
        // Refresh theme after update
        await fetchTheme();
        return { success: true, message: response.message };
      } else {
        throw new Error(response.message || 'Failed to update theme');
      }
    } catch (err) {
      console.error('Error updating theme:', err);
      return { success: false, error: err.message };
    }
  }, [organisationId, fetchTheme]);

  /**
   * Reset theme to default
   */
  const resetTheme = useCallback(async () => {
    try {
      const response = await BackendAuth.apiCallJson(`/api/theme/${organisationId}`, {
        method: 'DELETE'
      });

      if (response.success) {
        // Refresh theme after reset
        await fetchTheme();
        return { success: true, message: response.message };
      } else {
        throw new Error(response.message || 'Failed to reset theme');
      }
    } catch (err) {
      console.error('Error resetting theme:', err);
      return { success: false, error: err.message };
    }
  }, [organisationId, fetchTheme]);

  /**
   * Get asset URL by type
   * @param {string} assetType - Asset type (logo_light, logo_dark, etc.)
   * @param {string} fallback - Fallback URL if asset not found
   * @returns {string} Asset URL
   */
  const getAssetUrl = useCallback((assetType, fallback = null) => {
    return assets[assetType] || fallback;
  }, [assets]);

  /**
   * Get logo URL based on current theme mode
   * @param {string} mode - 'light' or 'dark'
   * @returns {string} Logo URL
   */
  const getLogoUrl = useCallback((mode = 'light') => {
    const assetType = mode === 'dark' ? 'logo_dark' : 'logo_light';
    return getAssetUrl(
      assetType,
      mode === 'dark' ? '/media/app/xoffi-logo-dark.png' : '/media/app/xoffi-logo-white.png'
    );
  }, [getAssetUrl]);

  /**
   * Apply CSS variables to DOM
   * @param {Object} variables - CSS variables object
   */
  const applyThemeToDOM = (variables) => {
    if (typeof window === 'undefined') return;

    const root = document.documentElement;

    Object.entries(variables).forEach(([key, value]) => {
      root.style.setProperty(key, value);
    });
  };

  /**
   * Toggle the active theme class on <html> (e.g. `theme-technic`).
   * Theme-scoped stylesheets (css/themes/<name>.css) key off this class.
   * @param {string|null} baseTheme - base theme name, or null/empty to clear
   */
  const applyThemeClass = (baseTheme) => {
    if (typeof window === 'undefined') return;

    const root = document.documentElement;
    const managed = ['modern', 'classic', 'minimal', 'corporate', 'creative', 'technic', 'aqua'];

    managed.forEach((name) => root.classList.remove(`theme-${name}`));

    const name = (baseTheme || '').toLowerCase();
    if (name) root.classList.add(`theme-${name}`);
  };

  /**
   * Load default theme (fallback)
   */
  const loadDefaultTheme = () => {
    const defaultCssVariables = {
      '--color-primary': '#009ef7',
      '--color-secondary': '#6c757d',
      '--color-accent': '#50cd89',
      '--bg-light': '#ffffff',
      '--bg-dark': '#1e1e2d',
      '--font-family': 'Inter',
      '--sidebar-width': '250px',
      '--header-height': '64px'
    };

    setCssVariables(defaultCssVariables);
    applyThemeToDOM(defaultCssVariables);
    applyThemeClass(null);
  };

  // Fetch theme on mount and when organisationId changes
  useEffect(() => {
    fetchTheme();
  }, [fetchTheme]);

  return {
    theme,
    assets,
    cssVariables,
    isLoading,
    error,
    fetchTheme,
    updateTheme,
    resetTheme,
    getAssetUrl,
    getLogoUrl
  };
}


/**
 * Hook to fetch available base theme templates
 * @returns {Object} Theme templates data
 */
export function useThemeTemplates() {
  const [templates, setTemplates] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchTemplates = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await BackendAuth.apiCallJson('/api/theme/templates/list');

      if (response.success) {
        setTemplates(response.themes || {});
      } else {
        throw new Error(response.message || 'Failed to fetch theme templates');
      }
    } catch (err) {
      console.error('Error fetching theme templates:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Get full configuration for a specific template
   * @param {string} templateName - Template name (modern, classic, etc.)
   * @returns {Object} Full theme configuration
   */
  const getTemplate = useCallback(async (templateName) => {
    try {
      const response = await BackendAuth.apiCallJson(`/api/theme/templates/${templateName}`);

      if (response.success) {
        return response.theme;
      } else {
        throw new Error(response.message || 'Failed to fetch template');
      }
    } catch (err) {
      console.error('Error fetching theme template:', err);
      return null;
    }
  }, []);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  return {
    templates,
    isLoading,
    error,
    fetchTemplates,
    getTemplate
  };
}


/**
 * Hook to validate theme configuration
 * @returns {Function} Validation function
 */
export function useThemeValidation() {
  const [isValidating, setIsValidating] = useState(false);

  const validateTheme = useCallback(async (themeData) => {
    try {
      setIsValidating(true);

      const response = await BackendAuth.apiCallJson('/api/theme/validate', {
        method: 'POST',
        body: JSON.stringify(themeData)
      });

      return {
        valid: response.valid || false,
        errors: response.errors || null,
        validatedConfig: response.validated_config || null
      };
    } catch (err) {
      console.error('Error validating theme:', err);
      return {
        valid: false,
        errors: err.message,
        validatedConfig: null
      };
    } finally {
      setIsValidating(false);
    }
  }, []);

  return {
    validateTheme,
    isValidating
  };
}
